import React, {createContext, useContext, useEffect, useState} from "react";
import {useSearchParams} from "react-router-dom";
import dataProvider from "../provider/api-interface";
import {t} from "i18next";
import jwt from "jwt-decode";
import {gifterPassword} from "../global";
import {useNavigate} from "react-router";
import {useAnalytics} from "../hook/useAnalytics";
import {isWishlistSoftDeleted} from "../utils/wishlistStatus";

interface Characteristics {
    characteristic: string
}

interface GalleryImages{
    image_thumb_uri: string;
    image_zoom_uri: string;
}

interface Tags {
    tag: string
}

interface Variations{
    available: number;
    code: string;
    created_at: string;
    id: string;
    price_currency: string;
    price_value: string;
    product_code: string;
    size_label: string;
    sku: string;
    tracking_brand: string;
    tracking_category: string;
    tracking_dimension_16: string;
    tracking_dimension_25: string;
    tracking_dimension_48: string;
    tracking_dimension_50: string;
    tracking_dimension_52: string;
    tracking_gender: string;
    tracking_id: string;
    tracking_mid_category: string;
    tracking_name: string;
    tracking_price: string;
    tracking_sub_category: string;
    tracking_top_category: string;
    tracking_variant: string;
}

// Let's define the type for a product
interface Product {
    id: string;
    created_at: string;
    product_code: string;
    title: string;
    subtitle: string;
    available: number;
    best_selection: number;
    catalog_availability: number;
    category_id: string;
    characteristics: Characteristics[];
    description: string;
    gallery_images: GalleryImages[];
    image_url: string;
    link_redirect: null|string;
    main_category_id: number;
    quantity_purchased: number;
    quantity_selected: number;
    size_and_fit: string;
    sub_category_id: number;
    sub_universe: string;
    tags: Tags[];
    variation_sku: string;
    variations: Variations[];
    list_code: string
}

interface CartProduct extends Product {
    gift_quantity: number; //Amount not arriving from server
}

// Let's define the type for the cart context
interface CabanaContextType {
    jwtToken: string | null;
    saveJwtToken: (jwtToken: string) => void;
    user: any | null;
    userType: string;
    cart: CartProduct[];
    addToCart: (product: Product) => void;
    removeFromCart: (productId: string) => void;
    removeAllFromCart: () => void;
    updateGiftQuantity: (productId: string, quantity: number) => void;
    isCartOpen: boolean;
    openCart: () => void;
    closeCart: () => void;
    listCode: any;
    wishlist: any;
    isLoadingWishlist: any;
    addProductToWishlist: (product: any, quantity: string) => {};
    removeProductToWishlist: (product: any) => {};
    forceRefreshWishlist: () => {};
    alertOpen: boolean;
    contextSetAlertOpen: (isOpen: boolean) => void;
    alertText: string;
    contextSetAlertText: (text: string) => void;
    alertType: "error" | "success";
    contextSetAlertType: (type: "error" | "success") => void;
}

// We create the context with a default value
const CabanaContext = createContext<CabanaContextType | undefined>(undefined);

// Custom hook to use context more easily
export const useCabanaContext = (): CabanaContextType => {
    const context = useContext(CabanaContext);
    if (!context) {
        throw new Error("useCabanaContext must be used inside a CartProvider");
    }
    return context;
};

// Provider that manages the cart status
export const CabanaProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { trackEvent } = useAnalytics();
    const windowLocationPathName = window.location.pathname.split('/');
    const userType = windowLocationPathName[1]; // "giftee" o "gifter"
    const routh = windowLocationPathName[2];

    let listCode: any = null;
    //right logic to get the list code
    switch (routh){
        case 'registry':
            if(userType === 'giftee'){
                listCode = (searchParams.get('id')) ? searchParams.get('id') : windowLocationPathName[3]
            }else{
                const utmSource = searchParams.get('utm_source');
                listCode = windowLocationPathName[3];
                if (utmSource) {
                    setTimeout(() => {
                        trackEvent("view_shared_registry", {
                            category: "cabana_event",
                            label: "A visitor views a shared gift list",
                            visit_source: utmSource,
                            registry_id: listCode
                        });
                    }, 2000);
                    searchParams.delete('utm_source');

                    const newUrl =
                        window.location.pathname + (searchParams.toString() ? `?${searchParams.toString()}` : '');

                    window.history.replaceState({}, '', newUrl);
                }
            }
            break;
        case 'product':
            listCode = searchParams.get('wishlistId')
            break;
        case 'finalize-gift':
            listCode = searchParams.get('listCode')
            break;
    }

    const [jwtToken, setJwtToken] = useState<string | null>(
        localStorage.getItem('token') || sessionStorage.getItem('token')
    );
    const [isJwtTokenChecked, setIsJwtTokenChecked] = useState<boolean>(false);

    const [user, setUser] = useState<any>(null);

    // Auto login only if jwtToken is null and userType is 'gifter'
    useEffect(() => {
        const autoLogin = async () => {
            if (!jwtToken && userType === 'gifter') {
                try {
                    const response = await dataProvider.gifterLogin(gifterPassword, listCode);
                    const token: string = response.token;
                    localStorage.setItem('token', token);
                    setJwtToken(token);
                } catch (error) {
                    console.log(error);
                    navigate(`/${userType}/404`);
                }
            }
        };

        autoLogin().then(() => {});
    }, [jwtToken, userType]);


    // Decode the token when it changes
    useEffect(() => {
        if (jwtToken) {
            setUser(jwt(jwtToken));
            setIsJwtTokenChecked(true)
        }
    }, [jwtToken]);

    // Check if the 'giftee' needs to be redirected
    useEffect(() => {
        if (isJwtTokenChecked && userType === 'giftee' && !user) {
            navigate('/login');
        }
    }, [user, userType, isJwtTokenChecked]);


    const [wishlist, setWishlist] = useState<any>(null);
    const [reloadWishlist, setReloadWishlist] = useState<number>(0)
    const [isLoadingWishlist, setLoadingWishlist] = useState<boolean>(true)
    const [alertOpen, setAlertOpen] = useState<boolean>(false);
    const [alertText, setAlertText] = useState<string>("");
    const [alertType, setAlertType] = useState<"error" | "success">("error");

    //Load data from API when a provider is mounted
    useEffect(() => {
        if(listCode !== null && jwtToken !== null){
            const fetchWishlist = async () => {
                try {
                    const response = await dataProvider.getWishlistByListCode(listCode, jwtToken);
                    setWishlist(response);
                    setLoadingWishlist(false)
                } catch (error: any) {
                    console.error("Error retrieving wishlist", error);
                }
            };

            fetchWishlist().then(() => {});
        }
    }, [reloadWishlist, listCode, isJwtTokenChecked]);


    useEffect(() => {
        if (wishlist == null) {
            return;
        }
        if (isWishlistSoftDeleted(wishlist)) {
            removeAllFromCart();
            navigate(userType === 'giftee' ? '/giftee/list-unavailable-message' : '/list-unavailable-message');
            return;
        }
        if (wishlist.end_date) {
            const now = new Date();
            const endDate = new Date(wishlist.end_date);

            if (now >= endDate) {
                removeAllFromCart();
                navigate(`/${userType}/expired-list-message`);
            }
        }
    }, [wishlist]);

    const [cart, setCart] = useState<CartProduct[]>(() => {
        //On startup, retrieve cart from localStorage
        const storedCart = localStorage.getItem("cabana-cart");
        return storedCart ? JSON.parse(storedCart) : [];
    });

    //Save cart to localStorage every time it changes
    useEffect(() => {
        localStorage.setItem("cabana-cart", JSON.stringify(cart));
    }, [cart]);

    const [isCartOpen, setIsCartOpen] = useState(false);

    // Function to add a product to the cart
    const addToCart = (product: Product) => {
        setCart((prevCart) => {
            // Let's check if the product is already in the cart
            const existingProduct = prevCart.find((p) => p.id === product.id);
            if (existingProduct) return prevCart; // Let's avoid duplicates

            return [
                ...prevCart,
                {
                    ...product,
                    gift_quantity: 1, // Initialize with 1
                },
            ];
        });
        setIsCartOpen(true); // Open cart when adding a product
    };

    // Remove a product from the cart
    const removeFromCart = (productId: string) => {
        setCart((prevCart) => prevCart.filter((product) => product.id !== productId));
    };

    const removeAllFromCart = () => {
        setCart([]);
    };

    const updateGiftQuantity = (productId: string, quantity: number) => {
        setCart((prevCart) =>
            prevCart.map((product) =>
                product.id === productId
                    ? {
                        ...product,
                        gift_quantity: Math.min(
                            Math.max(quantity, 1), // Minimum 1
                            product.quantity_selected - product.quantity_purchased // max
                        ),
                    }
                    : product
            )
        );
    };

    const openCart = () => setIsCartOpen(true);
    const closeCart = () => setIsCartOpen(false);

    //wishlist methods
    const addProductToWishlist = async (product: any, quantity: string) => {
        setLoadingWishlist(true);
        if(parseInt(quantity) === 0){
            setAlertText(t('productAddedQuantityError'));
            setAlertType("error");
            setAlertOpen(true);
            setLoadingWishlist(false);
            autoClose();
        }else{
            await dataProvider.addProductToWishlist(wishlist.id, product.id, product.variations[0].sku, true, jwtToken, quantity)
                .then((response: any) => {
                    if(parseInt(response.status) === 200 || parseInt(response.status) === 201) {
                        console.log(product)
                        trackEvent("add_to_registry", {
                            category: "cabana_event",
                            label: "User adds a product to the gift list",
                            product_id: product.variations[0].sku,
                            product_name: product.title,
                            cabana_category: product.main_category[0].name_en,
                            user_id: user.id,
                            giftee_id: user.id,
                            registry_id: listCode,
                        });
                        setAlertText(t('productAddedGl'));
                        setAlertType("success");
                        setAlertOpen(true);
                        autoClose();
                        setReloadWishlist(reloadWishlist + 1);
                    } else {
                        setAlertText(t('productAddedError'));
                        setAlertType("error");
                        setAlertOpen(true);
                        setLoadingWishlist(false);
                        autoClose();
                    }
                }).catch((error: any) => {
                    console.log(error)
                    setAlertText(t('productAddedError'));
                    setAlertType("error");
                    setAlertOpen(true);
                    autoClose();
                    setLoadingWishlist(false)
                });
        }
    }

    //handleConfirmDelete
    const removeProductToWishlist = async (product: any) => {
        setLoadingWishlist(true);
        await dataProvider.removeProductFromWishlist(wishlist.id, product.id, jwtToken)
            .then((response: any) => {
                if(parseInt(response.status) === 200 || parseInt(response.status) === 201) {
                    setAlertText(t('productRemoveOk'));
                    setAlertType("success");
                    setAlertOpen(true);
                    autoClose();
                    setReloadWishlist(reloadWishlist + 1);
                } else {
                    setAlertText(t('productRemoveKo'));
                    setAlertType("error");
                    setAlertOpen(true);
                    setLoadingWishlist(false);
                    autoClose();
                }
            }).catch((error: any) => {
                console.log(error)
                setAlertText(t('productRemoveKo'));
                setAlertType("error");
                setAlertOpen(true);
                autoClose();
                setLoadingWishlist(false)
            });
    }

    const contextSetAlertOpen = (isCartOpen: boolean) => {
        setAlertOpen(isCartOpen)
    }

    const contextSetAlertText = (text: string) => {
        setAlertText(text)
    }


    const contextSetAlertType = (type: "error" | "success") => {
        setAlertType(type)
    }

    const autoClose = () => {
        setTimeout(() => {
            contextSetAlertOpen(false);
        }, 3000);
    };

    const saveJwtToken = (jwtToken: string) => {
        localStorage.setItem('token', jwtToken);
        setJwtToken(jwtToken);
    }

    const forceRefreshWishlist = () =>{
        setReloadWishlist(reloadWishlist + 1);
        return true
    }


    return (
        <CabanaContext.Provider value={{
            jwtToken,
            saveJwtToken,
            user,
            userType,
            cart,
            addToCart,
            removeFromCart,
            removeAllFromCart,
            updateGiftQuantity,
            isCartOpen,
            openCart,
            closeCart,
            listCode,
            wishlist,
            isLoadingWishlist,
            addProductToWishlist,
            removeProductToWishlist,
            forceRefreshWishlist,
            alertOpen,
            contextSetAlertOpen,
            alertText,
            contextSetAlertText,
            alertType,
            contextSetAlertType
        }}>
            {children}
        </CabanaContext.Provider>
    );
};
